/**
 * 대기실과 숙지 화면.
 *
 * 지금까지 이 게임은 이름과 방 코드를 넣으면 **바로 3D 안**이었다.
 * 배역도 없고 대본도 없고 누가 들어와 있는지도 모르는 채로 조사가 시작돼 있었다.
 *
 * 그 앞에 두 단계를 넣는다.
 *
 *   lobby   사람이 모인다. 누가 들어와 있는지 보인다. 호스트가 배역을 나눈다
 *   read    각자 자기 배역과 대본을 받는다. 다 읽으면 준비를 누른다
 *           전원이 누르면 호스트가 시작할 수 있다
 *
 * **대본은 서버가 자기 것만 내려준다.** `t:'script'` 로 요청하면 그 사람의
 * 배역 대본만 온다. 클라이언트 번들에는 대본이 한 글자도 안 들어간다 —
 * 미리보기 페이지가 그것을 어겨서 여섯 부가 통째로 공개된 적이 있다.
 */

import { ep as epData } from './episodeData';
import { episodeText } from '../../shared/episode-text';
import type { Net } from './net';
import type { PlayerState } from '../../shared/protocol';
import { renderBook, type BookScript, type BookState, type Memos } from './book';
import './book.css';
import './lobby.css';

/** 여섯이 정원이다. 모자라도 시작은 되지만 배역이 빈다 */
const FULL = 6;

export class Lobby {
  private el: HTMLDivElement;
  private script: BookScript | null = null;
  private book: BookState = { tab: 'script', page: 0 };
  private reading = false;
  private asked = false;
  /** 매 프레임 innerHTML 을 다시 쓰면 버튼이 계속 떨어져 나가 클릭이 씹힌다 */
  private sig = '';
  /** 인물마다 내가 적어 둔 것. 방마다 따로 남는다 */
  private memos: Memos = {};

  constructor(private net: Net) {
    try {
      this.memos = JSON.parse(localStorage.getItem(this.memoKey()) ?? '{}');
    } catch {
      this.memos = {};
    }
    this.el = document.createElement('div');
    this.el.id = 'lobby-stage';
    document.getElementById('ui')!.appendChild(this.el);

    this.el.addEventListener('click', (e) => this.onClick(e));
    // 메모는 칠 때마다 저장한다. 다시 그리지는 않는다 — 커서가 튄다
    this.el.addEventListener('input', (e) => {
      const t = e.target as HTMLTextAreaElement;
      const id = t.dataset?.memo;
      if (!id) return;
      this.memos[id] = t.value;
      localStorage.setItem(this.memoKey(), JSON.stringify(this.memos));
      this.sig = this.signature(); // 지문만 갱신해 재렌더를 막는다
    });
    net.on('script', (s) => {
      this.script = { castId: s.castId, culprit: s.culprit, sections: s.sections };
      this.render(true);
    });
  }

  /** 매 프레임 호출된다. 단계가 바뀌면 알아서 뜨고 사라진다 */
  update() {
    const ph = this.net.world.phase;
    const on = ph === 'lobby' || ph === 'pick' || ph === 'read';
    this.el.style.display = on ? '' : 'none';
    if (!on) {
      this.asked = false;
      return;
    }
    // 배역을 받았는데 아직 대본을 안 받았으면 한 번만 요청한다
    if (ph === 'read' && !this.asked && this.net.world.roles[this.net.you]) {
      this.asked = true;
      this.net.send({ t: 'script' });
    }
    this.render();
  }

  /** 화면에 실제로 드러나는 것만 모은 지문. 이게 그대로면 다시 안 그린다 */
  private signature() {
    const w = this.net.world;
    return [
      w.phase,
      this.reading ? `book:${this.book.tab}${this.book.page}${this.book.open ?? ''}` : '',
      this.script ? 'sc' : '',
      this.roster()
        .map((p) => `${p.id}|${p.name}|${p.color}|${p.host ? 'h' : ''}|${w.roles[p.id] ?? ''}|${w.ready[p.id] ? 'r' : ''}`)
        .join(','),
    ].join('#');
  }

  private onClick(e: MouseEvent) {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-act],[data-tab],[data-page],[data-cast-open]');
    if (!b) return;
    if (b.dataset.tab) {
      this.book.tab = b.dataset.tab as BookState['tab'];
      this.book.page = 0;
      this.book.open = undefined;
      return this.render(true);
    }
    if (b.dataset.page) {
      this.book.page = Math.max(0, Number(b.dataset.page));
      return this.render(true);
    }
    if (b.dataset.castOpen !== undefined) {
      this.book.open = b.dataset.castOpen || undefined;
      return this.render(true);
    }
    switch (b.dataset.act) {
      case 'roles':
        this.net.send({ t: 'roles' });
        break;
      case 'pick':
        this.net.send({ t: 'pick', castId: b.dataset.cast! });
        break;
      case 'toread':
        this.net.send({ t: 'phase', v: 'read' });
        break;
      case 'read':
        this.reading = true;
        break;
      case 'close':
        this.reading = false;
        break;
      case 'ready':
        this.net.send({ t: 'ready', v: !this.net.world.ready[this.net.you] });
        break;
      case 'start':
        this.net.send({ t: 'phase', v: 'investigate' });
        break;
    }
    this.render(true);
  }

  private memoKey() {
    return `cs.memo.${this.net.room}`;
  }

  /** 접속 직후 roster 가 비는 구간이 있다. 그때도 나는 이 방에 있다 */
  private roster(): PlayerState[] {
    const list = [...this.net.players.values()];
    if (list.length || !this.net.you) return list;
    return [{ id: this.net.you, name: '나', color: '#8ab4ff', host: true }];
  }

  private render(force = false) {
    const sig = this.signature();
    if (!force && sig === this.sig) return;
    this.sig = sig;

    const w = this.net.world;
    const me = this.net.you;
    const players = this.roster();
    // players 가 아직 안 왔으면 isHost() 가 false 다. 그 구간에는 방장으로 본다 —
    // 아니면 「시작하기」가 아무에게도 안 보인다
    const amHost = this.net.players.size ? this.net.isHost() : true;

    if (this.reading && this.script) {
      const cast: Record<string, string> = {};
      for (const p of players) {
        const c = w.roles[p.id];
        if (c) cast[c] = p.name;
      }
      this.el.innerHTML =
        `<div class="lb-book">${renderBook(this.book, this.script, cast, [], this.memos)}` +
        `<button class="lb-close" data-act="close">닫기</button></div>`;
      return;
    }

    const n = players.length;
    const roster = players
      .map((p) => this.seat(p, w.roles[p.id], !!w.ready[p.id], !!p.host, p.id === me))
      .join('');

    this.el.innerHTML =
      w.phase === 'lobby'
        ? this.lobbyView(n, roster, amHost)
        : w.phase === 'pick'
          ? this.pickView(amHost)
          : this.readView(roster, amHost);
  }

  private seat(p: PlayerState, castId: string | undefined, ready: boolean, isHost: boolean, isMe: boolean) {
    const c = castId ? epData().cast.byId[castId] : null;
    // **남의 배역은 감추지 않는다.** 크라임씬도 누가 누구인지는 다 안다.
    // 감추는 것은 각자의 대본이지 이름표가 아니다
    return `<div class="lb-seat ${isMe ? 'me' : ''} ${ready ? 'ready' : ''}">
      <span class="lb-dot" style="background:${p.color}"></span>
      <span class="lb-nm">${esc(p.name)}${isHost ? ' <b>방장</b>' : ''}</span>
      ${c ? `<span class="lb-cast">${esc(c.name)} · ${c.age}</span>` : '<span class="lb-cast wait">배역 대기</span>'}
      ${ready ? '<span class="lb-ok">읽음</span>' : ''}
    </div>`;
  }

  private lobbyView(n: number, roster: string, amHost: boolean) {
    return `<div class="mn">
      <div class="mn-left">
        <div class="mn-eyebrow">CRIME SCENE</div>
        <h1 class="mn-title">${episodeText(this.net.world.episode).title}</h1>
        <div class="mn-lead">${episodeText(this.net.world.episode).long}</div>
        <div class="mn-room">방 코드 <b>${esc(this.net.room)}</b> — 같은 코드를 넣으면 여기서 만납니다</div>
        ${
          amHost
            ? `<button class="mn-start" data-act="roles">시작하기</button>
               <div class="mn-hint">${
                 n < FULL
                   ? `${n}명이 들어와 있습니다. 여섯이 다 안 와도 시작할 수 있습니다.`
                   : '여섯이 다 모였습니다.'
               }</div>`
            : `<div class="mn-hint">방장이 시작하기를 누르면 배역을 고릅니다.</div>`
        }
      </div>

      <div class="mn-right">
        <div class="mn-rhead">들어온 사람 <b>${n}</b><span>/ ${FULL}</span></div>
        <div class="mn-list">${roster}</div>
        ${Array.from({ length: Math.max(0, FULL - n) })
          .map(() => '<div class="lb-seat empty">비어 있음</div>')
          .join('')}
      </div>
    </div>`;
  }

  /** 배역 선택. 먼저 고른 사람이 가져간다 */
  private pickView(amHost: boolean) {
    const w = this.net.world;
    const me = this.net.you;
    const mine = w.roles[me];
    const owner: Record<string, string> = {};
    for (const p of this.net.players.values()) {
      const c = w.roles[p.id];
      if (c) owner[c] = p.name;
    }
    const un = [...this.net.players.values()].filter((p) => !w.roles[p.id]).length;

    const cards = epData().cast.all.filter((c) => c.role !== 'victim')
      .map((c, i) => {
        const by = owner[c.id];
        const isMine = mine === c.id;
        // 흩뿌린 각도는 **id 로 고정한다.** 매번 난수를 쓰면 다시 그릴 때마다
        // 카드가 튄다. 던져 놓은 것처럼 보이되 움직이지는 않아야 한다
        const t = tilt(c.id);
        const sp = SPOT[c.id] ?? [12 + (i % 3) * 30, 12 + Math.floor(i / 3) * 44];
        return `<button class="pk-card ${isMine ? 'mine' : by ? 'taken' : ''}"
                  style="--tilt:${t.deg}deg; --dx:${t.dx}px; --dy:${t.dy}px; --i:${i};
                         --px:${sp[0]}%; --py:${sp[1]}%"
                  data-act="pick" data-cast="${c.id}" ${by && !isMine ? 'disabled' : ''}>
          <div class="pk-art">
            <img src="/cards/${c.id}.png" alt="" loading="lazy"
                 onerror="this.style.display='none';this.parentElement.classList.add('noart')" />
            <span class="pk-initial">${esc(c.name.slice(0, 1))}</span>
          </div>
          ${pinHtml(c.id)}
          <div class="pk-tag">${isMine ? '내 배역' : by ? esc(by) : ''}</div>
        </button>`;
      })
      .join('');

    return `<div class="pk-stage">
      <div class="pk-head">
        <div class="lb-title">배역을 고르세요</div>
        <div class="lb-sub">먼저 집는 사람이 가져갑니다. 여섯 중 하나가 범인인데
          지금은 아무도 모릅니다 — 집고 나면 자기 대본에만 적혀 있습니다.</div>
      </div>
      <div class="pk-table">${cards}</div>
      <div class="pk-foot">${
        amHost
          ? `<button class="lb-start" data-act="toread" ${un ? 'disabled' : ''}>
               ${un ? `${un}명이 아직 안 골랐습니다` : '대본 나눠주기'}
             </button>`
          : `<div class="lb-note">${un ? `${un}명이 아직 고르는 중입니다.` : '방장이 대본을 나눠줄 때까지 기다립니다.'}</div>`
      }</div>
    </div>`;
  }

  private readView(roster: string, amHost: boolean) {
    const w = this.net.world;
    const me = this.net.you;
    const castId = w.roles[me];
    const c = castId ? epData().cast.byId[castId] : null;
    const mineReady = !!w.ready[me];
    const ids = [...this.net.players.values()].filter((p) => w.roles[p.id]);
    const left = ids.filter((p) => !w.ready[p.id]).length;

    // 배역 선택과 같은 판 위에 이어진다. 내가 누구인지는 얼굴로 먼저 온다
    return `<div class="rd-stage">
      <div class="rd-file">
        <div class="rd-left">
          <div class="rd-photo">
            ${c ? `<img src="/cards/${c.id}.png" alt="" />` : ''}
            <i class="pk-pin"></i>
          </div>
        </div>

        <div class="rd-right">
          <div class="rd-stamp">배역 배정</div>
          <div class="rd-name">${c ? esc(c.name) : '…'}</div>
          ${
            c
              ? `<div class="rd-job">${c.age} · ${esc(c.job)}</div>
                 <div class="rd-look-h">그 모습에서 알 수 있는 것</div>
                 <div class="rd-look">${esc(c.look ?? '')}</div>`
              : '<div class="lb-note">배역을 받는 중입니다.</div>'
          }

          <div class="lb-actions">
            <button class="lb-go" data-act="read" ${this.script ? '' : 'disabled'}>대본 읽기</button>
            <button class="lb-ready ${mineReady ? 'on' : ''}" data-act="ready" ${this.script ? '' : 'disabled'}>
              ${mineReady ? '읽었습니다 ✓' : '다 읽으면 누르세요'}
            </button>
          </div>

          <div class="lb-roster">${roster}</div>

          ${
            amHost
              ? `<button class="lb-start" data-act="start" ${left ? 'disabled' : ''}>
                   ${left ? `${left}명이 아직 읽는 중` : '시작'}
                 </button>`
              : `<div class="lb-note">${left ? `${left}명이 아직 읽는 중입니다.` : '방장이 시작하면 들어갑니다.'}</div>`
          }

          <div class="lb-hint">숙지 시간은 정해져 있지 않습니다. 다 읽었으면 누르세요.</div>
        </div>
      </div>
    </div>`;
  }
}

/**
 * 카드를 던져 놓은 각도. **id 로 고정**한다 — 매번 난수면 다시 그릴 때마다 튄다.
 * 테이블에 흩뿌린 것처럼 보이되, 한 번 놓인 자리는 안 움직여야 집을 수 있다.
 */
/** 카드를 붙여 둔 방식. id 로 고정해서 다시 그려도 안 바뀐다 */
/** 칠판 어디에 붙어 있는가. 줄을 맞추지 않는다 — 던져 놓은 판이다 */
const SPOT: Record<string, [number, number]> = {
  jung:   [9, 14],
  myeong: [33, 6],
  yuri:   [58, 17],
  bae:    [80, 5],
  yun:    [16, 52],
  song:   [61, 55],
};

function pinHtml(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 17 + id.charCodeAt(i)) >>> 0;
  const kind = h % 3;
  if (kind === 0) return '<i class="pk-pin"></i>';
  if (kind === 1) return `<i class="pk-tape ${h % 2 ? 'l' : 'r'}"></i>`;
  return '<i class="pk-clip"></i>';
}

function tilt(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const r = (k: number, span: number) => (((h >>> (k * 5)) % 1000) / 1000 - 0.5) * span;
  return { deg: +r(0, 17).toFixed(2), dx: Math.round(r(1, 90)), dy: Math.round(r(2, 74)) };
}

const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
